import type {
    ShippingFrecuency,
    ShippingType,
    SubscriptionData,
} from "@/services/types";

type SubscriptionDisplayDetails = {
    people: string;
    quantity: string;
    weight: string;
    productKind: "frutas" | "verduras" | "frutas y verduras";
    description: string;
};

const detailsByKey: Record<string, SubscriptionDisplayDetails> = {
    small: {
        people: "1-2",
        quantity: "10-12",
        weight: "5-6",
        productKind: "frutas y verduras",
        description:
            "Ideal para 1-2 personas. 10-12 variedades de frutas y verduras de temporada. Peso aprox. 5-6 Kg.",
    },
    medium: {
        people: "2-4",
        quantity: "12-14",
        weight: "7-8",
        productKind: "frutas y verduras",
        description:
            "Ideal para 2-4 personas. 12-14 variedades de frutas y verduras de temporada. Peso aprox. 7-8 Kg.",
    },
    large: {
        people: "4-6",
        quantity: "14-16",
        weight: "11-12",
        productKind: "frutas y verduras",
        description:
            "Ideal para 4-6 personas. 14-16 variedades de frutas y verduras de temporada. Peso aprox. 11-12 Kg.",
    },
    fruits: {
        people: "1-2",
        quantity: "5-7",
        weight: "5-6",
        productKind: "frutas",
        description:
            "Ideal para 1-2 personas. 5-7 variedades de frutas de temporada. Peso aprox. 5-6 Kg.",
    },
    vegetables: {
        people: "2-4",
        quantity: "14-16",
        weight: "7-8",
        productKind: "verduras",
        description:
            "Ideal para 2-4 personas. 14-16 variedades de verduras de temporada. Peso aprox. 7-8 Kg.",
    },
};

const resolveSubscriptionKey = (
    subscription?: Partial<SubscriptionData> | null,
) => {
    if (!subscription) return undefined;

    const id = String(subscription.id ?? "");
    const code = subscription.code?.toUpperCase() ?? "";
    const boxSize = subscription.box_size?.toUpperCase() ?? "";
    const name = subscription.name?.toUpperCase() ?? "";
    const tokens = [code, boxSize, name].join(" ");

    if (
        id === "5" ||
        tokens.includes("VEGETABLE") ||
        tokens.includes("VERDURA") ||
        code === "CV001"
    ) {
        return "vegetables";
    }
    if (
        id === "4" ||
        tokens.includes("FRUIT") ||
        tokens.includes("FRUTA") ||
        code === "CF001"
    ) {
        return "fruits";
    }
    if (
        id === "2" ||
        tokens.includes("BIG") ||
        tokens.includes("LARGE") ||
        tokens.includes("GRANDE")
    ) {
        return "large";
    }
    if (id === "1" || tokens.includes("MEDIUM") || tokens.includes("MEDIANA")) {
        return "medium";
    }
    if (
        id === "3" ||
        tokens.includes("SMALL") ||
        tokens.includes("CHICA") ||
        tokens.includes("PEQUE")
    ) {
        return "small";
    }

    return undefined;
};

export const getSubscriptionDisplayDetails = (
    subscription?: Partial<SubscriptionData> | null,
) => {
    const key = resolveSubscriptionKey(subscription);
    if (key) return detailsByKey[key];

    return {
        people: subscription?.aprox_people ?? "",
        quantity: subscription?.aprox_quantity ?? "",
        weight: subscription?.aprox_weight ?? "",
        productKind: "frutas y verduras",
        description: subscription?.description ?? "",
    } satisfies SubscriptionDisplayDetails;
};

export const getShippingTypeName = (shippingType?: ShippingType | string) => {
    if (shippingType === "DELIVERY") return "Entrega a domicilio";
    if (shippingType === "PICKUP") return "Retiro en tiendas";

    return shippingType ?? "";
};

export const getShippingFrecuencyName = (
    shippingFrecuency?: ShippingFrecuency | string,
) => {
    if (shippingFrecuency === "WEEKLY") return "Semanal";
    if (shippingFrecuency === "BIWEEKLY") return "Quincenal";

    return shippingFrecuency ?? "";
};
